{
  "title": "AI credits",
  "description": "Each AI request uses credits from its server's balance. Every server's allowance resets on the 1st of the month.",
  "credits_one": "{{n}} credit",
  "credits_other": "{{n}} credits",
  "tabs": {
    "label": "AI credits sections",
    "overview": "Overview",
    "usage": "Usage log",
    "settings": "Top-up and caps"
  },
  "tasks": {
    "simple_question": "Simple question",
    "explain_error": "Explain an error",
    "diagnose_downtime": "Diagnose downtime",
    "fix_config": "Fix a config",
    "investigation": "Complex investigation"
  },
  "costs": {
    "fixed_one": "{{n}} credit",
    "fixed_other": "{{n}} credits",
    "range": "{{min}} to {{max}} credits",
    "metered": "Metered, up to your cap"
  },
  "actors": {
    "member": "Team member",
    "hosting_account": "Hosting account",
    "automation": "Automatic"
  },
  "automations": {
    "downtime-monitor": "Downtime monitor"
  },
  "trial": {
    "title": "Trial allowance",
    "body": "During the trial you get the full {{plan}} allowance: {{credits}} credits until {{date}}."
  },
  "totals": {
    "left": "Credits left this month, all servers",
    "resets": "Allowance resets",
    "resetsHint": "Bought credits carry over until used.",
    "autoTopUp": "Auto top-up",
    "autoOn": "On",
    "autoOff": "Off",
    "autoSpent": "This month:",
    "autoSetup": "Set up auto top-up"
  },
  "servers": {
    "title": "By server",
    "left": "Left",
    "trialAllowance": "Trial allowance",
    "trial": "Trial",
    "low": "{{pct}} used. Buy a pack or turn on auto top-up so AI help doesn't stop.",
    "out": "No credits left. AI requests on this server are paused until you buy credits or the allowance resets.",
    "notActive": "AI features start once the server is activated.",
    "paused": "AI features are paused on this server because it hasn't reached bPanel for a while.",
    "resets": "Resets on",
    "trialEnds": "Trial ends",
    "cap": "Investigation cap",
    "capValue": "{{n}} credits",
    "capCustom": "custom",
    "viewUsage": "View usage",
    "empty": {
      "title": "No servers yet",
      "body": "Each server comes with a monthly AI credit allowance.",
      "action": "Add a server"
    },
    "lowAuto": "{{pct}} used. Auto top-up buys more when it falls under 10%.",
    "lowPaused": "{{pct}} used. Auto top-up is paused on this server until its failed payment is paid.",
    "locked": "AI features are paused because the control panel is locked. Renew to bring them back; your remaining credits are kept."
  },
  "usage": {
    "title": "Usage by task",
    "description": "{{from}} to {{to}}, with the published cost of each task.",
    "server": "Server",
    "allServers": "All servers",
    "requests_one": "{{n}} request",
    "requests_other": "{{n}} requests",
    "total": "Total this month",
    "costsNote": "Simple questions and error explanations cost 1 credit. Complex investigations are metered and stop at your cap.",
    "daily": "Credits per day, this month",
    "monthly": "Credits per month",
    "view": "Chart view",
    "days": "Days",
    "months": "Months",
    "noHistory": "No earlier months yet.",
    "chartHint": "Hover a bar for its total."
  },
  "purchases": {
    "title": "Recent purchases",
    "description": "Packs of 100 credits, bought by hand or by auto top-up.",
    "auto": "Auto top-up",
    "manual": "Bought by a team member",
    "states": {
      "processing": "Processing",
      "paid": "Paid",
      "failed": "Failed",
      "refunded": "Refunded",
      "void": "Void",
      "pending": "Awaiting payment"
    },
    "empty": {
      "title": "No purchases yet",
      "body": "Credit packs you buy show here with their invoices."
    }
  },
  "buy": {
    "action": "Buy credits",
    "title": "Buy AI credits",
    "description": "Credits come in packs of {{size}} and go to one server. They carry over until used.",
    "server": "Server",
    "serverHint": "{{left}} credits left on this server",
    "packs": "Packs",
    "credits_one": "{{n}} credit",
    "credits_other": "{{n}} credits",
    "unit": "Pack of {{size}} credits at today's price",
    "price": "Price before VAT",
    "note": "The pack price applies to every new purchase; packs you bought earlier keep what you paid.",
    "continue": "Continue",
    "noServers": "You need an active server to buy credits.",
    "notNow": "Not now",
    "withVat": "Total incl. VAT {{rate}}",
    "noVat": "Total, no VAT outside Saudi Arabia"
  },
  "log": {
    "caption": "AI usage log",
    "search": "Search by subject",
    "cols": {
      "date": "Date",
      "task": "Task",
      "server": "Server",
      "actor": "Requested by",
      "credits": "Credits"
    },
    "filters": {
      "server": "Server",
      "task": "Task"
    },
    "spentOfCap": "{{spent}} of {{cap}}",
    "running": "Running",
    "stopped": "Stopped at cap",
    "export": "Export CSV",
    "exported_one": "Exported {{n}} row",
    "exported_other": "Exported {{n}} rows",
    "note": "Shows this month's requests. Totals of earlier months are on the overview.",
    "empty": {
      "title": "No AI requests this month",
      "body": "Requests from your team, hosting accounts and automatic checks show here."
    }
  },
  "settings": {
    "save": "Save",
    "discard": "Discard",
    "saved": "AI credit settings saved",
    "topUp": {
      "title": "Auto top-up",
      "description": "Buy packs automatically so AI help doesn't stop in the middle of the month.",
      "enable": "Top up automatically",
      "rule": "When a server falls under {{pct}} of its monthly allowance, we buy {{credits}} credits for it with your default payment method.",
      "packs": "Packs each time",
      "packsHint": "{{size}} credits a pack",
      "exclVat": "before VAT",
      "limit": "Monthly limit",
      "limitHint": "The most auto top-up can spend in a calendar month, before VAT. From 5 to 2,000.",
      "sar": "SAR",
      "spent": "Spent this month:",
      "noMethod": "Auto top-up needs a saved payment method. It's saved with your first payment.",
      "readOnly": "Only the owner, admins and billing members can change auto top-up."
    },
    "caps": {
      "title": "Investigation cap",
      "description": "Complex investigations are metered. They stop when they reach the cap, so one task never spends more than you allow.",
      "default": "Default cap",
      "defaultHint": "For every server without its own cap. From 10 to 5,000 credits.",
      "unit": "credits",
      "perServer": "Per server",
      "perServerHint": "Leave a server empty to use the default.",
      "serverLabel": "Cap for {{server}}",
      "usesDefault": "Default ({{n}})",
      "readOnly": "Only the owner, admins and billing members can change the cap."
    }
  }
}
